import { useCallback, useState } from 'react';

import { logger } from 'adapters/Logger';

type StorageOperation = 'read' | 'write' | 'remove';

// Browsers that block storage (privacy modes, sandboxed contexts) throw a
// SecurityError on any access. That is the user's environment, not a bug: the
// hook already falls back to the initial value. A page mounts several hooks and
// each one would report the same error, so the blocked-storage error is logged
// once per page load (module level, shared by every instance). It stays an
// error, not a warning: `Logger.warning` does not call Sentry, and Sentry is
// the only place that shows how many users have storage blocked.
let blockedStorageLogged = false;

function isStorageBlocked(error: unknown): boolean {
  return (
    typeof error === 'object' &&
    error !== null &&
    (error as { name?: unknown }).name === 'SecurityError'
  );
}

function logStorageError(
  error: unknown,
  operation: StorageOperation,
  info: Record<string, unknown>
) {
  const err =
    error instanceof Error
      ? error
      : new Error(`SessionStorage error on key "${info.key}"`);
  const tags = { flow: 'session-storage', operation };
  const extra = {
    ...info,
    ...(!(error instanceof Error) && { thrownValue: String(error) }),
  };

  if (isStorageBlocked(error)) {
    if (blockedStorageLogged) return;
    blockedStorageLogged = true;
  }

  logger.error(err, tags, extra);
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export default function useSessionStorage<T = any>(
  key: string,
  initialValue?: T
): [T, (value: T) => void, () => void] {
  const [state, setState] = useState<T>(() => {
    let storedValue: string | null = null;
    try {
      if (typeof window === 'undefined' || !window.sessionStorage) {
        return initialValue;
      }

      storedValue = window.sessionStorage.getItem(key);

      if (!storedValue || storedValue === 'undefined') {
        return initialValue;
      }

      return JSON.parse(storedValue);
    } catch (error) {
      logStorageError(error, 'read', { key, storedValue });
      return initialValue;
    }
  });

  const setValue = useCallback(
    (value: T) => {
      let serializedValue: string | undefined;
      try {
        if (typeof window === 'undefined' || !window.sessionStorage) {
          return;
        }

        setState(value);
        serializedValue = JSON.stringify(value);

        if (serializedValue === undefined) {
          window.sessionStorage.removeItem(key);
          return;
        }

        window.sessionStorage.setItem(key, serializedValue);
      } catch (error) {
        logStorageError(error, 'write', { key, serializedValue });
      }
    },
    [key]
  );

  const removeValue = useCallback(() => {
    try {
      if (typeof window === 'undefined' || !window.sessionStorage) {
        return;
      }

      window.sessionStorage.removeItem(key);
      setState(initialValue);
    } catch (error) {
      logStorageError(error, 'remove', { key });
    }
  }, [key, initialValue]);

  return [state, setValue, removeValue];
}
